import { useTranslation } from "react-i18next";
import styles from "./InvitationStatusTab.module.css";
import LoadingSpinner from "../../UI/LoadingSpinner/LoadingSpinner";

export default function InvitationStatusTab({
  invitations,
  loadingUsers,
  usersError,
  fetchUsers,
  hasFetchedRef,
  handleCancelInvitation,
}) {
  const { t } = useTranslation("common");

  const invitationStatusBadgeClass = (status) => {
    switch (status) {
      case "pending":
        return styles.statusBadgePending;
      case "used":
        return styles.statusBadgeUsed;
      case "cancelled":
        return styles.statusBadgeCancelled;
      default:
        return styles.statusBadgeExpired;
    }
  };

  const renderInvitationStatus = (invitation, iconSize) => {
    switch (invitation.status) {
      case "pending":
        return (
          <>
            <svg
              width={iconSize}
              height={iconSize}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
            >
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" />
            </svg>
            {t("userInfoPage.invitationStatus.status.pending")}
          </>
        );
      case "used":
        return (
          <>
            <svg
              width={iconSize}
              height={iconSize}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
            >
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            {t("userInfoPage.invitationStatus.status.used")}
          </>
        );
      case "cancelled":
        return (
          <>
            <svg
              width={iconSize}
              height={iconSize}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
            </svg>
            {t("userInfoPage.invitationStatus.status.cancelled")}
          </>
        );
      default:
        return (
          <>
            <svg
              width={iconSize}
              height={iconSize}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
            {t("userInfoPage.invitationStatus.status.expired")}
          </>
        );
    }
  };

  return (
    <div className={styles.tabContent}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>
            {t("userInfoPage.invitationStatus.title")}
          </h2>
          <p className={styles.sectionDescription}>
            {t("userInfoPage.invitationStatus.description")}
          </p>
        </div>
        <button
          type="button"
          className={styles.headerActionButton}
          onClick={() => {
            hasFetchedRef.current = false;
            fetchUsers();
          }}
        >
          {t("userInfoPage.invitationStatus.refreshInvitations")}
        </button>
      </div>

      {loadingUsers && <LoadingSpinner />}

      {usersError && (
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>
            {t("common.error")}: {usersError}
          </p>
          <button
            type="button"
            className={styles.retryButton}
            onClick={() => {
              hasFetchedRef.current = false;
              fetchUsers();
            }}
          >
            {t("common.retry")}
          </button>
        </div>
      )}

      {!loadingUsers &&
        !usersError &&
        invitations &&
        invitations.length > 0 && (
          <>
            <div
              className={`${styles.tableContainer} ${styles.tableContainerDesktopOnly}`}
            >
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>
                      {t("userInfoPage.invitationStatus.tableHeaders.email")}
                    </th>
                    <th>
                      {t("userInfoPage.invitationStatus.tableHeaders.status")}
                    </th>
                    <th>
                      {t("userInfoPage.invitationStatus.tableHeaders.created")}
                    </th>
                    <th>
                      {t("userInfoPage.invitationStatus.tableHeaders.expires")}
                    </th>
                    <th>
                      {t(
                        "userInfoPage.invitationStatus.tableHeaders.invitedBy"
                      )}
                    </th>
                    <th>
                      {t("userInfoPage.invitationStatus.tableHeaders.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((invitation) => (
                    <tr key={invitation._id}>
                      <td className={styles.emailCell}>{invitation.email}</td>
                      <td className={styles.statusCell}>
                        <span
                          className={`${styles.statusBadge} ${invitationStatusBadgeClass(
                            invitation.status
                          )}`}
                        >
                          {renderInvitationStatus(invitation, 16)}
                        </span>
                      </td>
                      <td className={styles.joinedCell}>
                        {invitation.createdAt
                          ? new Date(invitation.createdAt).toLocaleDateString()
                          : t("common.notAvailable")}
                      </td>
                      <td className={styles.joinedCell}>
                        {invitation.expiresAt
                          ? new Date(invitation.expiresAt).toLocaleDateString()
                          : t("common.notAvailable")}
                      </td>
                      <td className={styles.emailCell}>
                        {invitation.createdBy?.name ||
                          invitation.createdBy?.email ||
                          t("common.notAvailable")}
                      </td>
                      <td className={styles.actionsCell}>
                        <div className={styles.actionButtons}>
                          {invitation.status === "pending" && (
                            <button
                              type="button"
                              className={styles.cancelInvitationButton}
                              onClick={() =>
                                handleCancelInvitation(
                                  invitation._id,
                                  invitation.email
                                )
                              }
                            >
                              {t(
                                "userInfoPage.invitationStatus.actions.cancel"
                              )}
                            </button>
                          )}
                          {invitation.status === "cancelled" && (
                            <span className={styles.disabledActionText}>
                              {t(
                                "userInfoPage.invitationStatus.status.cancelled"
                              )}
                            </span>
                          )}
                          {invitation.status === "used" && (
                            <span className={styles.disabledActionText}>
                              {t("userInfoPage.invitationStatus.status.used")}
                            </span>
                          )}
                          {invitation.status === "expired" && (
                            <span className={styles.disabledActionText}>
                              {t(
                                "userInfoPage.invitationStatus.status.expired"
                              )}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.mobileCardLayout}>
              <div className={styles.invitationsGrid}>
                {invitations.map((invitation) => (
                  <div key={invitation._id} className={styles.invitationCard}>
                    <div className={styles.invitationHeader}>
                      <div className={styles.invitationEmail}>
                        {invitation.email}
                      </div>
                      <span
                        className={`${styles.statusBadge} ${invitationStatusBadgeClass(
                          invitation.status
                        )}`}
                      >
                        {renderInvitationStatus(invitation, 14)}
                      </span>
                    </div>

                    <div className={styles.invitationMeta}>
                      <div className={styles.metaItem}>
                        <span className={styles.metaLabel}>
                          {t("userInfoPage.invitationStatus.labels.created")}
                        </span>
                        <span className={styles.metaValue}>
                          {invitation.createdAt
                            ? new Date(
                                invitation.createdAt
                              ).toLocaleDateString()
                            : t("common.notAvailable")}
                        </span>
                      </div>
                      <div className={styles.metaItem}>
                        <span className={styles.metaLabel}>
                          {t("userInfoPage.invitationStatus.labels.expires")}
                        </span>
                        <span className={styles.metaValue}>
                          {invitation.expiresAt
                            ? new Date(
                                invitation.expiresAt
                              ).toLocaleDateString()
                            : t("common.notAvailable")}
                        </span>
                      </div>
                      <div className={styles.metaItem}>
                        <span className={styles.metaLabel}>
                          {t("userInfoPage.invitationStatus.labels.invitedBy")}
                        </span>
                        <span className={styles.metaValue}>
                          {invitation.createdBy?.name ||
                            invitation.createdBy?.email ||
                            t("common.notAvailable")}
                        </span>
                      </div>
                    </div>

                    <div className={styles.cardActions}>
                      <div className={styles.actionButtons}>
                        {invitation.status === "pending" && (
                          <button
                            type="button"
                            className={`${styles.cancelInvitationButton} ${styles.cancelInvitationButtonFullWidth}`}
                            onClick={() =>
                              handleCancelInvitation(
                                invitation._id,
                                invitation.email
                              )
                            }
                          >
                            {t(
                              "userInfoPage.invitationStatus.actions.cancel"
                            )}
                          </button>
                        )}
                        {invitation.status === "cancelled" && (
                          <span className={styles.disabledActionText}>
                            {t(
                              "userInfoPage.invitationStatus.status.cancelled"
                            )}
                          </span>
                        )}
                        {invitation.status === "used" && (
                          <span className={styles.disabledActionText}>
                            {t("userInfoPage.invitationStatus.status.used")}
                          </span>
                        )}
                        {invitation.status === "expired" && (
                          <span className={styles.disabledActionText}>
                            {t(
                              "userInfoPage.invitationStatus.status.expired"
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

      {!loadingUsers &&
        !usersError &&
        (!invitations || invitations.length === 0) && (
          <div className={styles.noUsers}>
            <p>{t("userInfoPage.invitationStatus.noInvitations")}</p>
            <button
              type="button"
              className={styles.noUsersButton}
              onClick={() => {
                hasFetchedRef.current = false;
                fetchUsers();
              }}
            >
              {t("userInfoPage.invitationStatus.refreshInvitations")}
            </button>
          </div>
        )}
    </div>
  );
}
